import { State } from 'xstate';
import { VideoEditorStateContext } from 'components/VideoTemplateEditor/context';
import { NavigationMachineContextType } from 'components/VideoTemplateEditor/state/fsm/navigation-machine';
import { NavigationMachineEvent } from 'components/VideoTemplateEditor/types/navigation-actions';
import { getAspectRatioName } from 'utils/aspect-ratio';
import { getLayerIdByAssetId } from '../../modules/layers/selectors';
import { FramingMethod, VideoTemplateStateContent } from '../../types';
import { MediaLayerResult } from './types';

export function hasMedia(state: VideoTemplateStateContent) {
  const { mainImage, slideshow, videoClips } = state;

  return (
    !!mainImage?.ids.find(currId => slideshow?.data[currId]?.imageSrc) ||
    !!slideshow?.order?.length ||
    !!videoClips?.order?.length
  );
}

export function hasText(state: VideoTemplateStateContent) {
  const { slideEffectText = {}, textOverlays } = state;
  const hasEffectText = Object.keys(slideEffectText).length > 0;
  const hasOverlayText = textOverlays?.order.length > 0;

  return hasEffectText || hasOverlayText;
}

export const getMediaLayerFramingMethod = (
  mediaLayer: MediaLayerResult,
): FramingMethod | undefined => {
  if (
    mediaLayer?.type === 'videoClip' &&
    mediaLayer?.asset?.integrationData?.id === 'dynamic'
  ) {
    return mediaLayer?.asset?.framingMethod;
  }

  return undefined;
};

export function getMediaLayer(
  state: VideoTemplateStateContent,
  layerId: string,
): MediaLayerResult {
  const { slideshow, videoClips } = state;

  // First, it tries to find a slide using the provided layer id. If it is found,
  // type is set to image and id/asset are set using the slideId and the slideshow
  // asset associated to it.
  const slideId = slideshow.order.find(
    id => slideshow.data[id]?.layerId === layerId,
  );

  if (slideId) {
    return { type: 'image', asset: slideshow.data[slideId], id: slideId };
  }

  // If the first condition is not met, it looks for a videoclip that matches the
  // provided layer id. If found, type is set to video and id/asset to the ones of
  // that particular asset.
  const videoClipId = videoClips.order.find(
    id => videoClips.data[id]?.layerId === layerId,
  );

  if (videoClipId) {
    return {
      type: 'videoClip',
      asset: videoClips.data[videoClipId],
      id: videoClipId,
    };
  }

  // Finally if none of the above is matched, type is set to none and no asset/id
  // is set.
  return { type: 'none' };
}

export function isDynamicAutoframedMediaLayer(
  state: VideoTemplateStateContent,
  layerId: string,
) {
  const { aspectRatio } = state;
  const mediaLayer = getMediaLayer(state, layerId);
  const framingMethod = getMediaLayerFramingMethod(mediaLayer);

  return (
    mediaLayer?.type === 'videoClip' &&
    mediaLayer?.asset?.integrationData?.id === 'dynamic' &&
    framingMethod === 'autoframe' &&
    getAspectRatioName(aspectRatio) !== 'landscape'
  );
}

function selecteLayerIdSelector(
  state: VideoTemplateStateContent,
  editorState: VideoEditorStateContext,
) {
  const { selectedAsset } = editorState;
  return getLayerIdByAssetId(state, selectedAsset.id);
}

export function isCaptionsViewOpen(
  navState: State<NavigationMachineContextType, NavigationMachineEvent>,
): boolean {
  return navState?.matches({ child: 'captions' });
}

export function isSelectedDynamicAutoframedMediaLayer(
  state: VideoTemplateStateContent,
  editorState: VideoEditorStateContext,
) {
  const selectedLayerId = selecteLayerIdSelector(state, editorState);
  return isDynamicAutoframedMediaLayer(state, selectedLayerId);
}
